import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, ExternalLink, Link2 } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const URL_RE = /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"])/gi;

// Auto-grows with content; "Expand" opens a full-screen writing surface for long messages.
export function GrowTextarea({ value, onChange, placeholder = "Share an update…", testid = "dispatch-body-input", minRows = 7 }) {
  const ref = useRef(null);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minRows * 24)}px`;
  }, [value, minRows]);
  const count = (value || "").length;
  return (
    <div className="relative">
      <Textarea ref={ref} data-testid={testid} placeholder={placeholder} rows={minRows}
        className="rounded-xl text-base leading-relaxed pr-10 pb-7 resize-none overflow-hidden"
        value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" data-testid={`${testid}-expand`} onClick={() => setFull(true)} aria-label="Expand editor"
        className="absolute top-2 right-2 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-[var(--brand-navy)] hover:bg-black/5 transition-colors">
        <Maximize2 className="h-4 w-4" />
      </button>
      <span className="absolute bottom-2 right-3 text-[11px] text-muted-foreground tabular-nums">{count}</span>
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-none w-screen h-[100dvh] sm:h-[90vh] sm:w-[min(760px,95vw)] rounded-none sm:rounded-3xl p-0 flex flex-col gap-0" data-testid={`${testid}-fullscreen`}>
          <DialogHeader className="px-5 pt-5 pb-3 border-b flex-row items-center justify-between space-y-0">
            <div>
              <DialogTitle className="font-heading text-[var(--brand-navy)]">Write your message</DialogTitle>
              <DialogDescription className="text-xs">{count} characters</DialogDescription>
            </div>
            <Button data-testid={`${testid}-collapse`} onClick={() => setFull(false)} className="rounded-full accent-bg text-white h-9 px-4 hover:opacity-90 mr-6">
              <Minimize2 className="h-4 w-4 mr-1.5" /> Done
            </Button>
          </DialogHeader>
          <textarea data-testid={`${testid}-full`} autoFocus placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
            className="flex-1 w-full resize-none bg-transparent px-5 py-4 text-[17px] leading-relaxed focus:outline-none" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LinkFields({ form, setForm, prefix = "dispatch" }) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--brand-navy)]/25 p-3 space-y-2" data-testid={`${prefix}-link-fields`}>
      <div className="flex items-center gap-1.5 text-sm font-medium text-[var(--brand-navy)]"><Link2 className="h-4 w-4" /> Call-to-action button <span className="text-muted-foreground font-normal">(optional)</span></div>
      <Input data-testid={`${prefix}-link-url-input`} placeholder="https://example.com/your-page" inputMode="url" className="rounded-xl h-11"
        value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground shrink-0">Button text</Label>
        <Input data-testid={`${prefix}-link-label-input`} placeholder="Click Here" maxLength={40} className="rounded-xl h-10"
          value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} />
      </div>
      <p className="text-[11px] text-muted-foreground">Shows a big tappable button under your message — much harder to miss than a link in the text.</p>
    </div>
  );
}

export function CtaButton({ url, label, accent, testid = "dispatch-cta-btn" }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="mt-4 flex items-center justify-center gap-2 w-full rounded-full text-white font-semibold text-[15px] h-12 shadow-md hover:opacity-90 active:scale-[0.98] transition-[opacity,transform]"
      style={{ background: accent || "var(--tenant-accent)" }}>
      {label || "Click Here"} <ExternalLink className="h-4 w-4" />
    </a>
  );
}

// Renders plain text with URLs turned into tappable links.
export function LinkedText({ text, className, testid }) {
  const parts = String(text || "").split(URL_RE);
  return (
    <p className={className} data-testid={testid}>
      {parts.map((part, i) => {
        if (i % 2 === 0) return part;
        const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
        return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="accent-text underline underline-offset-2 break-all" onClick={(e) => e.stopPropagation()}>{part}</a>;
      })}
    </p>
  );
}
